/**
 * TanStack Query defaults.
 *
 * The defaults matter more than they look: they are what stops a legal CRM showing
 * a lawyer a stale case stage, and what stops the app hammering the API with retries
 * of requests the kernel has already, deliberately, refused.
 */

import { QueryClient, type QueryClientConfig } from '@tanstack/react-query';

import { type ApiError, isApiError } from '../errors';

/**
 * A refused request is a decision, not a fault. Retrying a 403 or a 404 will get the
 * same answer, cost the user a delay, and write another denial into the audit log —
 * and repeated CROSS_TENANT denials from one actor are treated as a security signal
 * (Step 3 §4). Retry only what could plausibly succeed on a second attempt.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (isApiError(error)) {
    const status = (error as ApiError).status;
    if (status === 401 || status === 403 || status === 404 || status === 409) return false;
    if (status === 400 || status === 422) return false;
    if (status === 429) return false; // Respect the limiter rather than fight it.
  }
  return failureCount < 2;
}

export const defaultQueryOptions: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Matter data changes under you while you read it — another fee earner
      // transitions a case, finance verifies a payment. Thirty seconds is short
      // enough to stay honest and long enough to avoid a request per keystroke.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      throwOnError: false,
    },
    mutations: {
      // Never retry a write automatically. Several of these endpoints move money,
      // and `Idempotency-Key` is required rather than universal — a blind retry of
      // a payment verification without one is a duplicate payment.
      retry: false,
    },
  },
};

export function createQueryClient(overrides?: QueryClientConfig): QueryClient {
  return new QueryClient({
    ...defaultQueryOptions,
    ...overrides,
    defaultOptions: {
      ...defaultQueryOptions.defaultOptions,
      ...overrides?.defaultOptions,
    },
  });
}
